import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { orchestratorApi, PipelineStats, CaseItem, DispatchItem, DashboardState, DASHBOARD_STATES } from '../services/orchestratorApi';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Play,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Inbox,
  Search,
  FileCheck,
  Loader2,
  Ban,
  ExternalLink,
  Zap,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

// State configuration for visual styling
const STATE_CONFIG: Record<DashboardState, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'INTAKE': { label: 'Intake', color: 'text-gray-700', bgColor: 'bg-gray-100', icon: Inbox },
  'GATHERING': { label: 'Gathering', color: 'text-blue-700', bgColor: 'bg-blue-100', icon: FileText },
  'READY_FOR_ANALYSIS': { label: 'Ready for Analysis', color: 'text-indigo-700', bgColor: 'bg-indigo-100', icon: Search },
  'ANALYZING': { label: 'Analyzing', color: 'text-purple-700', bgColor: 'bg-purple-100', icon: Loader2 },
  'ISSUES_PENDING': { label: 'Issues Pending', color: 'text-amber-700', bgColor: 'bg-amber-100', icon: AlertTriangle },
  'READY_FOR_CALCULATION': { label: 'Ready for Calculation', color: 'text-emerald-700', bgColor: 'bg-emerald-100', icon: FileCheck },
  'COMPLETE': { label: 'Complete', color: 'text-green-700', bgColor: 'bg-green-100', icon: CheckCircle }
};

// Action labels for dispatch items
const ACTION_LABELS: Record<string, string> = {
  'run_unified_analysis': 'Run Unified Analysis',
  'run_forensic': 'Run Forensic Analysis',
  'run_attorney': 'Run Attorney Analysis',
  'run_adjuster': 'Run Adjuster Analysis',
  'run_settlement': 'Run Settlement Calculation',
  'reprocess_affected': 'Reprocess Affected Agents'
};

export default function OrchestratorDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [pipelineStats, setPipelineStats] = useState<PipelineStats | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [totalCases, setTotalCases] = useState(0);
  const [dispatchQueue, setDispatchQueue] = useState<DispatchItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedState, setSelectedState] = useState<DashboardState | null>(
    (searchParams.get('state') as DashboardState) || null
  );
  const [showDispatchQueue, setShowDispatchQueue] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [pipeline, casesData, dispatch] = await Promise.all([
        orchestratorApi.getPipelineStats(),
        orchestratorApi.getCases({ 
          state: selectedState || undefined,
          limit: 50 
        }),
        orchestratorApi.getDispatchQueue({ limit: 20 })
      ]);
      
      setPipelineStats(pipeline);
      setCases(casesData.cases);
      setTotalCases(casesData.total);
      setDispatchQueue(dispatch.items);
      setPendingCount(dispatch.summary.pending_count);
    } catch (err: any) {
      console.error('Failed to fetch orchestrator data:', err);
      setError(err.message || 'Failed to load orchestrator data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle state filter change
  const handleStateFilter = (state: DashboardState | null) => {
    setSelectedState(state);
    if (state) {
      setSearchParams({ state });
    } else {
      setSearchParams({});
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Quick actions
  const handleRunAnalysis = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      await orchestratorApi.createDispatch(caseId, 'run_unified_analysis', 'HIGH');
      toast.success('Analysis job created');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create analysis job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveDispatch = async (dispatchId: string) => {
    setActionLoading(dispatchId);
    try {
      await orchestratorApi.approveDispatch(dispatchId);
      toast.success('Dispatch approved and queued');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectDispatch = async (dispatchId: string) => {
    setActionLoading(dispatchId);
    try {
      await orchestratorApi.cancelDispatch(dispatchId);
      toast.success('Dispatch cancelled');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel dispatch');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
          <span className="ml-2 text-gray-600">Loading orchestrator...</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="orchestrator-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Case Orchestrator</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage case pipeline and automated workflows
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/issues')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <AlertTriangle className="w-4 h-4" />
              Issue Tracker
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
            <button onClick={handleRefresh} className="ml-auto text-red-600 hover:text-red-800 underline">
              Retry
            </button>
          </div>
        )}

        {/* Pipeline Overview */}
        {pipelineStats && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 p-6" data-testid="pipeline-overview">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pipeline Overview</h2>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">
                  Total: <strong>{pipelineStats.total_cases}</strong> cases
                </span>
                {pipelineStats.total_stuck > 0 && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    <strong>{pipelineStats.total_stuck}</strong> stuck
                  </span>
                )}
              </div>
            </div>
            
            {/* Pipeline Funnel */}
            <div className="flex items-stretch gap-1" data-testid="pipeline-funnel">
              {DASHBOARD_STATES.map((state, idx) => {
                const stateData = pipelineStats.states.find(s => s.state === state);
                const config = STATE_CONFIG[state];
                const Icon = config.icon;
                const isSelected = selectedState === state;
                const count = stateData?.count || 0;
                const stuckCount = stateData?.stuck_count || 0;
                
                return (
                  <button
                    key={state}
                    onClick={() => handleStateFilter(isSelected ? null : state)}
                    className={`
                      flex-1 p-3 rounded-lg transition-all relative group
                      ${isSelected 
                        ? `${config.bgColor} ring-2 ring-offset-2 ring-gray-400` 
                        : 'bg-gray-50 hover:bg-gray-100'
                      }
                    `}
                    data-testid={`pipeline-state-${state.toLowerCase()}`}
                  >
                    <div className="flex flex-col items-center">
                      <Icon className={`w-5 h-5 ${config.color} mb-1`} />
                      <span className="text-2xl font-bold text-gray-900">{count}</span>
                      <span className="text-xs text-gray-500 text-center">{config.label}</span>
                      {stuckCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {stuckCount}
                        </span>
                      )}
                    </div>
                    {idx < DASHBOARD_STATES.length - 1 && (
                      <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-4 text-gray-300 z-10" />
                    )}
                  </button>
                );
              })}
            </div>
            
            {selectedState && (
              <div className="mt-3 flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  Filtered by: <strong>{STATE_CONFIG[selectedState].label}</strong>
                </span>
                <button 
                  onClick={() => handleStateFilter(null)}
                  className="text-sm text-blue-600 hover:underline ml-2"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Case List */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200" data-testid="case-list">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Cases {selectedState ? `(${STATE_CONFIG[selectedState].label})` : '(Active)'}
              </h2>
              <p className="text-sm text-gray-500">
                {totalCases} total • Click a case to view details
              </p>
            </div>
            
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {cases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No cases found</p>
                </div>
              ) : (
                cases.map((caseItem) => {
                  const stateConfig = STATE_CONFIG[caseItem.state];
                  const StateIcon = stateConfig.icon;
                  
                  return (
                    <div 
                      key={caseItem.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`case-item-${caseItem.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/report/${caseItem.id}`)}
                              className="font-medium text-gray-900 hover:text-blue-600 truncate"
                            >
                              {caseItem.name}
                            </button>
                            {caseItem.is_stuck && (
                              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Stuck
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className={`flex items-center gap-1 ${stateConfig.color}`}>
                              <StateIcon className="w-4 h-4" />
                              {stateConfig.label}
                            </span>
                            <span>•</span>
                            <span>{caseItem.days_in_state} days</span>
                            {caseItem.open_issues > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-amber-600">
                                  {caseItem.open_issues} issue{caseItem.open_issues > 1 ? 's' : ''}
                                </span>
                              </>
                            )}
                          </div>
                          
                          {/* Blocker/Next Action */}
                          <div className="mt-2 text-sm">
                            {caseItem.blocker.type === 'blocker' && (
                              <span className="text-red-600 flex items-center gap-1">
                                <Ban className="w-4 h-4" />
                                {caseItem.blocker.message}
                              </span>
                            )}
                            {caseItem.blocker.type === 'waiting' && (
                              <span className="text-purple-600 flex items-center gap-1">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {caseItem.blocker.message}
                              </span>
                            )}
                            {caseItem.blocker.type === 'action' && (
                              <span className="text-blue-600">
                                Next: {caseItem.blocker.message}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          {caseItem.state === 'READY_FOR_ANALYSIS' && (
                            <button
                              onClick={() => handleRunAnalysis(caseItem.id)}
                              disabled={actionLoading === caseItem.id}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                              data-testid={`run-analysis-${caseItem.id}`}
                            >
                              {actionLoading === caseItem.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                              Run Analysis
                            </button>
                          )}
                          {caseItem.state === 'ISSUES_PENDING' && (
                            <button
                              onClick={() => navigate(`/issues?report_id=${caseItem.id}`)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                              data-testid={`view-issues-${caseItem.id}`}
                            >
                              <AlertTriangle className="w-4 h-4" />
                              View Issues
                            </button>
                          )}
                          {caseItem.state === 'READY_FOR_CALCULATION' && (
                            <button
                              onClick={() => navigate(`/report/${caseItem.id}`)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                            >
                              <FileCheck className="w-4 h-4" />
                              Generate Report
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/report/${caseItem.id}`)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Dispatch Queue Sidebar */}
          <div className="lg:col-span-1" data-testid="dispatch-queue">
            <div className="bg-white rounded-xl border border-gray-200">
              <button
                onClick={() => setShowDispatchQueue(!showDispatchQueue)}
                className="w-full p-4 flex items-center justify-between border-b border-gray-200 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Pending Approvals</h2>
                  {pendingCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                      {pendingCount}
                    </span>
                  )}
                </div>
                {showDispatchQueue ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {showDispatchQueue && (
                <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                  {dispatchQueue.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-300" />
                      <p className="text-sm">No pending approvals</p>
                    </div>
                  ) : (
                    dispatchQueue.filter(d => d.status === 'PENDING_APPROVAL').map((dispatch) => (
                      <div key={dispatch.id} className="p-4" data-testid={`dispatch-item-${dispatch.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {ACTION_LABELS[dispatch.action] || dispatch.action}
                            </p>
                            <button
                              onClick={() => navigate(`/report/${dispatch.report_id}`)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              {dispatch.report_id}
                            </button>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            dispatch.priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                            dispatch.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {dispatch.priority}
                          </span>
                        </div>
                        
                        <p className="text-xs text-gray-500 mb-3">
                          Triggered by: {dispatch.trigger}
                        </p>
                        
                        {dispatch.affected_agents && dispatch.affected_agents.length > 0 && (
                          <div className="text-xs text-gray-500 mb-3">
                            Agents: {dispatch.affected_agents.join(', ')}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveDispatch(dispatch.id)}
                            disabled={actionLoading === dispatch.id}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            data-testid={`approve-dispatch-${dispatch.id}`}
                          >
                            {actionLoading === dispatch.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectDispatch(dispatch.id)}
                            disabled={actionLoading === dispatch.id}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                            data-testid={`reject-dispatch-${dispatch.id}`}
                          >
                            <Ban className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {dispatchQueue.filter(d => d.status !== 'PENDING_APPROVAL').length > 0 && (
                    <div className="p-3 bg-gray-50">
                      <p className="text-xs text-gray-500 text-center">
                        {dispatchQueue.filter(d => d.status !== 'PENDING_APPROVAL').length} other dispatches
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
